import { downloadFile } from "./download";
import {
  formatMoney,
  formatNumber,
  formatPeriod,
  lineItemsCsv,
  quantityWithUnit,
  reportFileStem,
  usageCsv,
  type BillingPeriodReport,
  type OverageReport,
  type ReportResult
} from "./report";

const BYTES_PER_MB = { decimal: 1_000_000, binary: 1_048_576 };

function ListSection({
  title,
  items,
  tone,
  empty
}: {
  title: string;
  items: string[];
  tone: "learn" | "fix" | "warn";
  empty?: string;
}) {
  if (items.length === 0 && !empty) return null;
  return (
    <div className={`insight ${tone}`}>
      <h4>{title}</h4>
      {items.length === 0 ? (
        <p className="muted">{empty}</p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Downloads({ result }: { result: ReportResult }) {
  const stem = reportFileStem(result);
  const report = result.report;
  return (
    <div className="downloads no-print">
      <button
        type="button"
        className="button primary"
        onClick={() => downloadFile(`${stem}.json`, JSON.stringify(result, null, 2), "application/json")}
      >
        Download JSON
      </button>
      {report && (
        <>
          <button
            type="button"
            className="button"
            onClick={() => downloadFile(`${stem}_line-items.csv`, lineItemsCsv(report), "text/csv")}
          >
            Line items CSV
          </button>
          <button
            type="button"
            className="button"
            onClick={() => downloadFile(`${stem}_usage.csv`, usageCsv(report), "text/csv")}
          >
            Usage CSV
          </button>
        </>
      )}
      <button type="button" className="button ghost" onClick={() => window.print()}>
        Print / Save as PDF
      </button>
    </div>
  );
}

function PeriodSection({ period, report }: { period: BillingPeriodReport; report: OverageReport }) {
  const mb = BYTES_PER_MB[report.byte_unit_system];
  return (
    <section className="period">
      <header className="period-header">
        <h3>
          {formatPeriod(period)}
          {period.partial_month && <span className="badge warn">Partial month</span>}
        </h3>
        <span className={period.overage_total > 0 ? "amount due" : "amount"}>
          {formatMoney(period.overage_total, report.currency)}
        </span>
      </header>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Term</th>
              <th className="num">Included</th>
              <th className="num">Used</th>
              <th className="num">Excess</th>
              <th className="num">Blocks</th>
              <th className="num">Rate</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {period.line_items.map((item) => (
              <tr key={item.label}>
                <td>{item.label}</td>
                <td className="num">{quantityWithUnit(item.included_quantity, item.unit)}</td>
                <td className="num">{quantityWithUnit(item.used_quantity, item.unit)}</td>
                <td className={item.excess_quantity > 0 ? "num over" : "num"}>
                  {quantityWithUnit(item.excess_quantity, item.unit)}
                </td>
                <td className="num">{formatNumber(item.overage_blocks, 0)}</td>
                <td className="num">
                  {formatMoney(item.overage_price, report.currency)} /{" "}
                  {quantityWithUnit(item.overage_block_size, item.block_unit)}
                  <span className="rounding">{item.block_rounding === "up" ? "started blocks" : "full blocks"}</span>
                </td>
                <td className="num strong">{formatMoney(item.amount, report.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <details className="usage-details">
        <summary>Usage by customer</summary>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Customer ID</th>
                <th className="num">Queries</th>
                <th className="num">Generations</th>
                <th className="num">Storage (MB)</th>
                <th className="num">Indexed (MB)</th>
                <th className="num">Users</th>
                <th className="num">Corpora</th>
              </tr>
            </thead>
            <tbody>
              {period.usage_by_customer.map((usage) => (
                <tr key={usage.customer_id}>
                  <td className="mono">{usage.customer_id}</td>
                  <td className="num">{formatNumber(usage.query_count)}</td>
                  <td className="num">{formatNumber(usage.generation_count)}</td>
                  <td className="num">{formatNumber(usage.storage_bytes / mb)}</td>
                  <td className="num">{formatNumber(usage.indexed_bytes / mb)}</td>
                  <td className="num">{formatNumber(usage.user_count)}</td>
                  <td className="num">{formatNumber(usage.corpus_count)}</td>
                </tr>
              ))}
              <tr className="total-row">
                <td>Total</td>
                <td className="num">{formatNumber(period.totals.query_count)}</td>
                <td className="num">{formatNumber(period.totals.generation_count)}</td>
                <td className="num">{formatNumber(period.totals.storage_bytes / mb)}</td>
                <td className="num">{formatNumber(period.totals.indexed_bytes / mb)}</td>
                <td className="num">{formatNumber(period.totals.user_count)}</td>
                <td className="num">{formatNumber(period.totals.corpus_count)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

export function ReportView({ result }: { result: ReportResult }) {
  const { agentOutput, report, inputs } = result;
  const failed = agentOutput.status === "failed" || report === null;
  return (
    <div className="report">
      <div className="report-head">
        <div>
          <p className="eyebrow">
            Overage report{result.revision > 1 && <span className="badge revision">Revision {result.revision}</span>}
          </p>
          <h2>{inputs.contractFileName}</h2>
          <p className="muted">
            {inputs.fromDate} → {inputs.toDate} · {inputs.customerIds.length} customer
            {inputs.customerIds.length === 1 ? "" : "s"} · session <span className="mono">{result.sessionKey}</span>
          </p>
        </div>
        <Downloads result={result} />
      </div>

      {failed ? (
        <div className="callout error">
          <strong>The report could not be produced.</strong>
          <p>{agentOutput.failure_reason ?? "The agent did not return priced usage."}</p>
        </div>
      ) : (
        <div className="stats">
          <div className="stat hero">
            <span className="stat-label">Total overage</span>
            <span className="stat-value">{formatMoney(report.grand_total_overage, report.currency)}</span>
          </div>
          {report.summary_by_term.map((term) => (
            <div className="stat" key={term.label}>
              <span className="stat-label">{term.label}</span>
              <span className="stat-value small">{formatMoney(term.amount, report.currency)}</span>
              <span className="stat-sub">{quantityWithUnit(term.excess_quantity, term.unit)} over</span>
            </div>
          ))}
        </div>
      )}

      {agentOutput.overage_summary && <p className="summary">{agentOutput.overage_summary}</p>}

      <div className="insights">
        <ListSection
          title="Lessons applied from memory"
          items={agentOutput.applied_lessons}
          tone="learn"
          empty="No earlier lessons applied to this contract."
        />
        <ListSection
          title="Self-check"
          items={[
            ...result.verificationIssues.map((issue) => `Checker rejected: ${issue}`),
            ...agentOutput.self_corrections
          ]}
          tone="fix"
          empty="Terms passed verification against the contract on the first attempt."
        />
        <ListSection title="Warnings" items={report?.warnings ?? []} tone="warn" />
        <ListSection title="New lessons for future reports" items={agentOutput.lessons_learned} tone="learn" />
      </div>

      <section className="card-section">
        <h3>Contract terms</h3>
        <p className="muted">{agentOutput.contract_summary}</p>
        <div className="terms">
          {agentOutput.contract_terms.map((term) => (
            <div className="term" key={term.label}>
              <div className="term-head">
                <strong>{term.label}</strong>
                <span className="badge">{term.metric}</span>
              </div>
              <p>
                {quantityWithUnit(term.included_quantity, term.included_unit)} included per month;{" "}
                {formatMoney(term.overage_price, term.currency)} per{" "}
                {quantityWithUnit(term.overage_block_size, term.block_unit)} over,{" "}
                {term.block_rounding === "up" ? "every started block billed" : "only full blocks billed"}.
              </p>
              <blockquote>{term.source_text}</blockquote>
            </div>
          ))}
        </div>
        {agentOutput.unpriced_contract_items.length > 0 && (
          <>
            <h4>Not priced</h4>
            <ul>
              {agentOutput.unpriced_contract_items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </>
        )}
      </section>

      {report && (
        <section className="card-section">
          <h3>Monthly breakdown</h3>
          {report.billing_periods.map((period) => (
            <PeriodSection key={period.period_start} period={period} report={report} />
          ))}
        </section>
      )}

      <section className="card-section">
        <h3>Notes for review</h3>
        <ul className="notes">
          {agentOutput.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
          {report?.assumptions.map((assumption) => (
            <li key={assumption} className="assumption">
              {assumption}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
